/* eslint-disable no-unused-vars -- TODO: remove this when we have a solution for the marker with label */
import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { DirectionsRenderer, Marker, GoogleMap } from 'react-google-maps';

import { MarkerClusterer } from 'react-google-maps/lib/components/addons/MarkerClusterer';

import { compose } from 'recompose';
import { withTheme } from 'styled-components';

import { googleMapsEnhance } from 'modules/google-maps/utils';

import { getDriverRouteToNextStop, getRouteForNonFinishedStops } from './utils';
import { mapOptions, directionsOptions, driverDirectionsOptions } from './options';
import { useAssignedDriverMarker, useStopMarkers, useDriverRouteInfoMarker } from './hooks';

import { getJobDetailData } from 'redux/modules/job-detail/selectors';

import { StyledWarning } from './styled';

const euopeCenter = { lat: 54.526, lng: 15.2551 };

const containerElement = <div style={{ height: window.innerHeight - 96 }} />;
const mapElement = <div style={{ height: window.innerHeight - 96 }} />;
const mapElementWithWarning = <div style={{ height: window.innerHeight - 136 }} />;

const enhance = compose(withTheme, googleMapsEnhance);

const AssignedMap = enhance(({ theme, setWarning, ...props }) => {
  const journey = useSelector(getJobDetailData);

  const {
    base: { colorSchema },
  } = theme;

  const { driver = {}, stops = [], shipper_orders_skin = {} } = journey || {};

  const [stopsDirections, setStopsDirections] = useState(null);
  const [driverDirections, setDriverDirections] = useState(null);

  const stopMarkers = useStopMarkers({ stops, shipper_orders_skin, colorSchema });
  const { assignedDriverMarker } = useAssignedDriverMarker(driver || {}, setWarning);
  const driverRouteInfoMarker = useDriverRouteInfoMarker(driverDirections);
  const mapRef = useRef(null);

  const handleMarkerClustererClick = (markerClusterer) => {
    const clickedMarkers = markerClusterer.getMarkers();
    const [marker] = clickedMarkers;

    const latlng = new window.google.maps.LatLng(
      marker.getPosition().lat() + 0.0001,
      marker.getPosition().lng() - 0.0001
    );

    marker.setPosition(latlng);
  };

  useEffect(() => {
    if (!stopMarkers.length || !mapRef || !mapRef.current) {
      setStopsDirections(null);
      return;
    }

    const route = getRouteForNonFinishedStops(stopMarkers);
    if (!route.origin || !route.destination) {
      setStopsDirections(null);
      return;
    }

    const DirectionsService = new window.google.maps.DirectionsService();

    DirectionsService.route(route, (result, status) => {
      if (status !== window.google.maps.DirectionsStatus.OK) {
        console.error(`error fetching directions ${result}`);
        setStopsDirections(null);
        return;
      }

      setStopsDirections(result);
    });
  }, [stopMarkers]);

  useEffect(() => {
    if (!stopMarkers.length || !assignedDriverMarker || !mapRef || !mapRef.current) {
      setDriverDirections(null);
      return;
    }

    setWarning(null);

    if (!assignedDriverMarker.accuratePosition) {
      setWarning('NO_ACCURATE_GPS');
    }

    if (assignedDriverMarker.dispatcher) {
      setWarning('IS_DISPATCHER');
    }

    const route = getDriverRouteToNextStop({ assignedDriverMarker, stopMarkers });
    if (!route.origin || !route.destination) {
      setDriverDirections(null);
      return;
    }

    const DirectionsService = new window.google.maps.DirectionsService();

    DirectionsService.route(route, (result, status) => {
      if (status !== window.google.maps.DirectionsStatus.OK) {
        console.error(`error fetching directions ${result}`);
        setDriverDirections(null);
        return;
      }

      setDriverDirections(result);
    });
  }, [stopMarkers, assignedDriverMarker, setWarning]);

  const hasDriverDirections = Boolean(driverDirections?.routes?.length);
  const hasStopsDirections = Boolean(stopsDirections?.routes?.length);
  const driverLeg = hasDriverDirections ? driverDirections.routes[0].legs?.[0] : null;

  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    if (!stopMarkers.length && !hasDriverDirections) {
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();

    stopMarkers.forEach(({ position }) => {
      if (typeof position?.lat === 'number' && typeof position?.lng === 'number') {
        bounds.extend(new window.google.maps.LatLng(position.lat, position.lng));
      }
    });

    if (hasDriverDirections) {
      const [route] = driverDirections.routes;

      if (route?.overview_path?.length) {
        route.overview_path.forEach((point) => {
          if (point instanceof window.google.maps.LatLng) {
            bounds.extend(point);
          } else if (typeof point?.lat === 'function' && typeof point?.lng === 'function') {
            bounds.extend(new window.google.maps.LatLng(point.lat(), point.lng()));
          } else if (typeof point?.lat === 'number' && typeof point?.lng === 'number') {
            bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
          }
        });
      } else if (route?.bounds) {
        const { south, west, north, east } = route.bounds;

        if (
          typeof south === 'number' &&
          typeof west === 'number' &&
          typeof north === 'number' &&
          typeof east === 'number'
        ) {
          bounds.extend(new window.google.maps.LatLng(south, west));
          bounds.extend(new window.google.maps.LatLng(north, east));
        }
      }
    }

    if (bounds.isEmpty()) {
      return;
    }

    mapRef.current.fitBounds(bounds);
  }, [stopMarkers, hasDriverDirections, driverDirections]);

  if (!journey) return <div></div>;

  return (
    <>
      <GoogleMap
        ref={mapRef}
        {...props}
        defaultZoom={4}
        defaultCenter={euopeCenter}
        defaultOptions={mapOptions}
      >
        <MarkerClusterer
          onClick={handleMarkerClustererClick}
          averageCenter
          enableRetinaIcons
          gridSize={1}
          defaultMaxZoom={15}
        >
          {stopMarkers.map((marker, index) => (
            <Marker key={`${marker.position.lat}-${marker.position.lng}-${index}`} {...marker} />
          ))}
        </MarkerClusterer>
        {assignedDriverMarker && <Marker {...assignedDriverMarker} />}
        {hasDriverDirections && driverRouteInfoMarker && (
          <Marker
            position={driverRouteInfoMarker.position}
            icon={{
              url:
                'data:image/svg+xml;charset=UTF-8,' +
                encodeURIComponent(`
                <svg width="80" height="40" xmlns="http://www.w3.org/2000/svg">
                  <rect width="80" height="40" fill="white" stroke="#3182ce" stroke-width="2" rx="4"/>
                  <text x="40" y="15" text-anchor="middle" font-family="Arial" font-size="10" fill="#3182ce" font-weight="bold">
                    ${driverLeg?.duration?.text ?? ''}
                  </text>
                  <text x="40" y="28" text-anchor="middle" font-family="Arial" font-size="10" fill="#3182ce" font-weight="bold">
                    ${driverLeg?.distance?.text ?? ''}
                  </text>
                </svg>
              `),
              scaledSize: new window.google.maps.Size(80, 40),
              anchor: new window.google.maps.Point(40, 40),
            }}
          />
        )}
        {hasStopsDirections && (
          <DirectionsRenderer
            directions={stopsDirections}
            options={directionsOptions(colorSchema)}
          />
        )}
        {hasDriverDirections && (
          <DirectionsRenderer directions={driverDirections} options={driverDirectionsOptions} />
        )}
      </GoogleMap>
    </>
  );
});

const AssignedDriverMap = () => {
  const [warning, setWarning] = useState(null);

  const warningOptions = {
    NO_DRIVER_GPS: `The driver's GPS position is not available`,
    NO_ACCURATE_GPS: `The driver's GPS position may not be accurate`,
    IS_DISPATCHER: `The assigned carrier user to this journey seems to be a dispatcher`,
  };

  return (
    <>
      <AssignedMap
        mapElement={warning ? mapElementWithWarning : mapElement}
        containerElement={containerElement}
        setWarning={setWarning}
      />
      {warning && <StyledWarning>{warningOptions[warning]}</StyledWarning>}
    </>
  );
};

export default AssignedDriverMap;
